// ABOUTME: Settings page component for user account management in Oráculo dos 12 Raios
// ABOUTME: Provides nickname editing and password change functionality with PocketBase integration

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDisplayName } from '../../utils/userDisplay';
import pb from '../../services/pocketbaseClient';
import AccountManager from '../../services/accountManager';
import feedbackService from '../../services/feedbackService';
import FeedbackButton from '../FeedbackButton/FeedbackButton';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import './Settings.css';

const Settings = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  // Refs for smooth scrolling (TD3-129 Phase 2.5)
  const topRef = useRef(null);
  const nicknameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Nickname editing state
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [editedNickname, setEditedNickname] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    hasSpecial: false,
    hasUpper: false,
    hasNumber: false,
    score: 0
  });

  // Account deletion state (TD3-122)
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Email change state (TD3-129)
  const [newEmail, setNewEmail] = useState('');
  const [passwordForEmailChange, setPasswordForEmailChange] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState({ type: '', text: '' });
  const [showLogoutOverlay, setShowLogoutOverlay] = useState(false);
  const [overlayFading, setOverlayFading] = useState(false);

  // Feedback history state (TD3-105)
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoadingFeedbacks, setIsLoadingFeedbacks] = useState(true);
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // TD3-129 Phase 2.5: Section visibility state
  const [visibleSection, setVisibleSection] = useState(null); // 'nickname', 'email', 'password', or null

  // Update nickname state when user changes
  useEffect(() => {
    if (user?.nickname !== undefined) {
      setNickname(user.nickname || '');
    }
  }, [user?.nickname]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (saveMessage.text) {
      const timer = setTimeout(() => {
        setSaveMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [saveMessage]);

  // Clear password messages after 5 seconds
  useEffect(() => {
    if (passwordMessage.text) {
      const timer = setTimeout(() => {
        setPasswordMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [passwordMessage]);

  // Clear email change messages after 5 seconds (TD3-129)
  useEffect(() => {
    if (emailChangeMessage.text) {
      const timer = setTimeout(() => {
        setEmailChangeMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [emailChangeMessage]);

  // Load feedback history (TD3-105)
  useEffect(() => {
    const loadFeedbackHistory = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingFeedbacks(true);
        const result = await feedbackService.getUserFeedbacks(user.id);

        if (result.success) {
          setFeedbacks(result.feedbacks || []);
        }
      } catch (error) {
        console.error('Error loading feedback history:', error);
      } finally {
        setIsLoadingFeedbacks(false);
      }
    };

    loadFeedbackHistory();
  }, [user?.id]);

  const validatePasswordStrength = (password) => {
    const length = password.length >= 8;
    const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    let score = 0;
    if (length) score += 1;
    if (hasSpecial) score += 1;
    if (hasUpper) score += 1;
    if (hasNumber) score += 1;

    return { length, hasSpecial, hasUpper, hasNumber, score };
  };

  // TD3-105: Format feedback date
  const formatFeedbackDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // TD3-105: Toggle feedback expansion
  const toggleFeedback = (feedbackId) => {
    setExpandedFeedback(expandedFeedback === feedbackId ? null : feedbackId);
  };

  // TD3-129 Phase 2.5: Smooth scroll helpers
  const scrollToSection = (sectionRef) => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // TD3-129 Phase 2.5: Show section and scroll to it
  const handleShowSection = (section) => {
    setVisibleSection(section);

    // Small delay to ensure section is rendered before scrolling
    setTimeout(() => {
      switch (section) {
        case 'nickname':
          scrollToSection(nicknameRef);
          break;
        case 'email':
          scrollToSection(emailRef);
          break;
        case 'password':
          scrollToSection(passwordRef);
          break;
        default:
          break;
      }
    }, 100);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleEditClick = () => {
    setEditedNickname(nickname);
    setIsEditing(true);
    setSaveMessage({ type: '', text: '' });
  };

  const handleCancelEdit = () => {
    setEditedNickname('');
    setIsEditing(false);
    setSaveMessage({ type: '', text: '' });
  };

  const handleSaveNickname = async () => {
    try {
      setIsSaving(true);
      setSaveMessage({ type: '', text: '' });

      // Trim and validate
      const trimmedNickname = editedNickname.trim();

      // Validate length
      if (trimmedNickname.length > 50) {
        setSaveMessage({ type: 'error', text: 'Nome deve ter no máximo 50 caracteres' });
        setIsSaving(false);
        return;
      }

      // Update PocketBase
      await pb.collection('OracleUsers').update(user.id, {
        nickname: trimmedNickname || null // Send null if empty to use email prefix
      });

      // Refresh user in AuthContext
      await refreshUser();

      // Update local state
      setNickname(trimmedNickname);
      setIsEditing(false);
      setEditedNickname('');
      setSaveMessage({ type: 'success', text: 'Nome atualizado com sucesso!' });

      // TD3-129 Phase 2.5: Scroll back to top and hide section
      setTimeout(() => {
        setVisibleSection(null);
        scrollToTop();
      }, 1500);
    } catch (error) {
      console.error('Error saving nickname:', error);
      setSaveMessage({
        type: 'error',
        text: error.message || 'Erro ao salvar nome. Tente novamente.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setIsChangingPassword(true);
      setPasswordMessage({ type: '', text: '' });

      // Validate all fields filled
      if (!currentPassword || !newPassword || !confirmPassword) {
        setPasswordMessage({ type: 'error', text: 'Todos os campos são obrigatórios' });
        setIsChangingPassword(false);
        return;
      }

      // Validate password length
      if (newPassword.length < 8) {
        setPasswordMessage({ type: 'error', text: 'A senha deve ter pelo menos 8 caracteres' });
        setIsChangingPassword(false);
        return;
      }

      // Validate passwords match
      if (newPassword !== confirmPassword) {
        setPasswordMessage({ type: 'error', text: 'As senhas não coincidem' });
        setIsChangingPassword(false);
        return;
      }

      // Update password in PocketBase
      await pb.collection('OracleUsers').update(user.id, {
        password: newPassword,
        passwordConfirm: confirmPassword,
        oldPassword: currentPassword
      });

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage({ type: 'success', text: 'Senha alterada com sucesso!' });

      // TD3-129 Phase 2.5: Scroll back to top and hide section
      setTimeout(() => {
        setVisibleSection(null);
        scrollToTop();
      }, 1500);
    } catch (error) {
      console.error('Error changing password:', error);

      // Check for specific error messages
      let errorText = 'Erro ao alterar senha. Tente novamente.';
      if (error.message && error.message.includes('Old password')) {
        errorText = 'Senha atual incorreta';
      } else if (error.message) {
        errorText = error.message;
      }

      // Clear password fields for security
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setPasswordMessage({ type: 'error', text: errorText });
    } finally {
      setIsChangingPassword(false);
    }
  };

  // TD3-129: Email change handler with immediate logout (LGPD compliant)
  const handleEmailChange = async (e) => {
    e.preventDefault();
    setIsChangingEmail(true);
    setEmailChangeMessage({ type: '', text: '' });

    try {
      const result = await AccountManager.changeEmail(
        user.id,
        user.email,
        newEmail,
        passwordForEmailChange
      );

      if (result.success) {
        console.log('✅ Email change requested successfully');

        // Show logout overlay with instructions
        setShowLogoutOverlay(true);

        // After 8 seconds, start fadeout
        setTimeout(() => {
          setOverlayFading(true);

          // During fadeout (2 seconds), logout and navigate
          setTimeout(() => {
            console.log('🚪 Logging out user for email change security');
            logout();
            navigate('/auth', {
              state: {
                message: 'E-mail de verificação enviado. Verifique sua nova caixa de entrada e clique no link de confirmação.'
              }
            });
          }, 2000); // 2 second fadeout
        }, 8000); // Show overlay for 8 seconds
      } else {
        setEmailChangeMessage({ type: 'error', text: result.error });
        setIsChangingEmail(false);
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailChangeMessage({ type: 'error', text: 'Erro ao alterar e-mail. Tente novamente.' });
      setIsChangingEmail(false);
    }
  };

  // TD3-122: Account deletion handlers
  const handleDeleteAccountClick = () => {
    setShowDeleteWarning(true);
    setDeleteError('');
  };

  const handleCancelDelete = () => {
    setShowDeleteWarning(false);
    setShowPasswordConfirm(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleContinueToPasswordConfirm = () => {
    setShowDeleteWarning(false);
    setShowPasswordConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeletingAccount(true);
      setDeleteError('');

      // Validate password field
      if (!deletePassword) {
        setDeleteError('Digite sua senha para confirmar');
        setIsDeletingAccount(false);
        return;
      }

      // Call AccountManager to handle deletion
      const result = await AccountManager.deleteAccount(
        user.id,
        user.email,
        deletePassword
      );

      if (!result.success) {
        setDeleteError(result.error || 'Erro ao excluir conta');
        setIsDeletingAccount(false);
        return;
      }

      // Success - show message and logout after delay
      alert(result.message || 'Sua conta foi marcada para exclusão. Você tem 30 dias para recuperá-la fazendo login novamente.');

      // Logout and redirect (logout function handles localStorage and authStore clearing)
      setTimeout(() => {
        logout(); // Use AuthContext logout function
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Account deletion error:', error);
      setDeleteError('Erro inesperado ao excluir conta. Entre em contato com o suporte.');
      setIsDeletingAccount(false);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="settings-container">
      {/* Mobile warning */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main settings for laptop screens */}
      <div className="settings-main">
        <header className="settings-header">
          <button
            onClick={handleBackToDashboard}
            className="back-btn secondary-cta"
          >
            ← Voltar ao Painel
          </button>
          <h1>MINHA CONTA</h1>
        </header>

        <main className="settings-content" ref={topRef}>
          {/* TD3-129 Phase 2.5: Consolidated Account Info with Change Buttons */}
          <section className="settings-section">
            <h2>Informações da Conta</h2>

            <div className="settings-field">
              <label>E-mail</label>
              <div className="field-value-with-action">
                <div className="field-value">{user.email}</div>
                <button
                  onClick={() => handleShowSection('email')}
                  className="change-btn secondary-cta"
                >
                  Alterar
                </button>
              </div>
            </div>

            <div className="settings-field">
              <label>Nome de Exibição</label>
              <div className="field-value-with-action">
                <div className="field-value">{getDisplayName(user)}</div>
                <button
                  onClick={() => handleShowSection('nickname')}
                  className="change-btn secondary-cta"
                >
                  Alterar
                </button>
              </div>
              <p className="field-hint">
                Como você aparece no Oráculo dos 12 Raios
              </p>
            </div>

            <div className="settings-field">
              <label>Senha</label>
              <div className="field-value-with-action">
                <div className="field-value">••••••••</div>
                <button
                  onClick={() => handleShowSection('password')}
                  className="change-btn secondary-cta"
                >
                  Alterar
                </button>
              </div>
            </div>
          </section>

          {/* TD3-129 Phase 2.5: Nickname Editing - Hidden by default */}
          {visibleSection === 'nickname' && (
            <section className="settings-section" ref={nicknameRef}>
              <h2>✨ Alterar Nome de Exibição</h2>

            {/* Save Message */}
            {saveMessage.text && (
              <div className={`save-message ${saveMessage.type}`}>
                {saveMessage.text}
              </div>
            )}

            {!isEditing ? (
              // Display Mode
              <div className="nickname-display">
                <div className="settings-field">
                  <label>Como você está sendo chamado:</label>
                  <div className="field-value">
                    {getDisplayName(user)}
                  </div>
                  <p className="field-hint">
                    {nickname ? 'Seu nome personalizado' : 'Usando prefixo do email (padrão)'}
                  </p>
                </div>
                <button
                  onClick={handleEditClick}
                  className="edit-btn primary-cta"
                >
                  Editar
                </button>
              </div>
            ) : (
              // Edit Mode
              <div className="nickname-edit">
                <div className="settings-field">
                  <label>Como gostaria de ser chamado?</label>
                  <input
                    type="text"
                    value={editedNickname}
                    onChange={(e) => setEditedNickname(e.target.value)}
                    placeholder="Digite seu nome ou apelido"
                    maxLength={50}
                    disabled={isSaving}
                    className="nickname-input"
                    autoFocus
                  />
                  <p className="field-hint">
                    {editedNickname.length}/50 caracteres • Deixe vazio para usar prefixo do email
                  </p>
                </div>
                <div className="edit-actions">
                  <button
                    onClick={handleSaveNickname}
                    disabled={isSaving}
                    className="save-btn primary-cta"
                  >
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="cancel-btn secondary-cta"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            </section>
          )}

          {/* TD3-129 Phase 2.5: Password Change - Hidden by default */}
          {visibleSection === 'password' && (
            <section className="settings-section" ref={passwordRef}>
              <h2>🔐 Alterar Senha</h2>

            {/* Password Message */}
            {passwordMessage.text && (
              <div className={`save-message ${passwordMessage.type}`}>
                {passwordMessage.text}
              </div>
            )}

            <div className="password-change">
              <div className="settings-field">
                <label>Senha Atual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Digite sua senha atual"
                  disabled={isChangingPassword}
                  className="password-input"
                />
              </div>

              <div className="settings-field">
                <label>Nova Senha</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordStrength(validatePasswordStrength(e.target.value));
                  }}
                  placeholder="Digite a nova senha"
                  disabled={isChangingPassword}
                  className="password-input"
                />
              </div>

              {newPassword && (
                <div className="password-requirements">
                  <div className="requirement-header">
                    <span>Requisitos da senha:</span>
                    {passwordStrength.score === 4 && (
                      <span className="strength-excellent">✨ Excelente segurança!</span>
                    )}
                    {passwordStrength.score === 3 && (
                      <span className="strength-good">👍 Boa segurança</span>
                    )}
                    {passwordStrength.score < 3 && passwordStrength.length && (
                      <span className="strength-basic">Mínimo atingido</span>
                    )}
                  </div>
                  <ul className="requirements-list">
                    <li className={passwordStrength.length ? 'met' : 'unmet'}>
                      {passwordStrength.length ? '✓' : '○'} Mínimo 8 caracteres
                    </li>
                    <li className={passwordStrength.hasUpper ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasUpper ? '✓' : '○'} Uma letra maiúscula (recomendado)
                    </li>
                    <li className={passwordStrength.hasNumber ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasNumber ? '✓' : '○'} Um número (recomendado)
                    </li>
                    <li className={passwordStrength.hasSpecial ? 'met recommended' : 'unmet recommended'}>
                      {passwordStrength.hasSpecial ? '✓' : '○'} Um caractere especial (recomendado)
                    </li>
                  </ul>
                </div>
              )}

              <div className="settings-field">
                <label>Confirmar Nova Senha</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme a nova senha"
                  disabled={isChangingPassword}
                  className="password-input"
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="change-password-btn primary-cta"
              >
                {isChangingPassword ? 'Atualizando...' : 'Atualizar Senha'}
              </button>
            </div>
            </section>
          )}

          {/* TD3-129 Phase 2.5: Email Change Section - Hidden by default */}
          {visibleSection === 'email' && (
            <section className="settings-section" ref={emailRef}>
              <h2>📧 Alterar E-mail</h2>

            {/* Email Change Message */}
            {emailChangeMessage.text && (
              <div className={`save-message ${emailChangeMessage.type}`}>
                {emailChangeMessage.text}
              </div>
            )}

            <p className="field-hint" style={{ marginBottom: '10px' }}>
              Atualize seu endereço de e-mail. Você receberá um link de verificação no novo e-mail.
            </p>

            <div style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              color: '#FFA000'
            }}>
              ⚠️ <strong>Importante:</strong> Você será desconectado automaticamente após solicitar a alteração de e-mail (requisito de segurança LGPD).
            </div>

            <form onSubmit={handleEmailChange} className="email-change-form">
              <div className="settings-field">
                <label>Novo E-mail</label>
                <input
                  type="email"
                  placeholder="Digite o novo e-mail"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="password-input"
                  disabled={isChangingEmail}
                  required
                />
              </div>

              <div className="settings-field">
                <label>Senha Atual (para confirmar)</label>
                <input
                  type="password"
                  placeholder="Digite sua senha atual"
                  value={passwordForEmailChange}
                  onChange={(e) => setPasswordForEmailChange(e.target.value)}
                  className="password-input"
                  disabled={isChangingEmail}
                  required
                />
              </div>

              <button
                type="submit"
                className="change-password-btn primary-cta"
                disabled={isChangingEmail || !newEmail || !passwordForEmailChange}
              >
                {isChangingEmail ? 'Processando...' : 'Alterar E-mail'}
              </button>
            </form>
            </section>
          )}

          {/* TD3-105: Feedback History - Always visible */}
          <section className="settings-section">
            <h2>💬 Seus Feedbacks Enviados</h2>

            {isLoadingFeedbacks ? (
              <div className="feedback-loading">Carregando histórico...</div>
            ) : feedbacks.length === 0 ? (
              <div className="feedback-empty">
                <p>Você ainda não enviou nenhum feedback.</p>
                <p className="hint">Use o ícone de feedback no canto superior esquerdo para compartilhar sua experiência!</p>
              </div>
            ) : (
              <div className="feedback-list">
                {feedbacks.map((feedback) => (
                  <div key={feedback.id} className="feedback-item">
                    <div className="feedback-header" onClick={() => toggleFeedback(feedback.id)}>
                      <div className="feedback-meta">
                        <span className="feedback-date">
                          {formatFeedbackDate(feedback.created)}
                        </span>
                        <span className={`feedback-type-badge ${feedback.feedbackType}`}>
                          {feedbackService.getFeedbackTypeDisplayName(feedback.feedbackType)}
                        </span>
                        <span
                          className="feedback-status-badge"
                          style={{
                            backgroundColor: feedbackService.getFeedbackStatusColor(feedback.status),
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          {feedbackService.getFeedbackStatusDisplayName(feedback.status)}
                        </span>
                      </div>
                      <div className="feedback-snippet">
                        {feedback.feedbackText.substring(0, 100)}
                        {feedback.feedbackText.length > 100 && '...'}
                      </div>
                      <button className="feedback-expand-btn">
                        {expandedFeedback === feedback.id ? '▼ Menos' : '▶ Mais'}
                      </button>
                    </div>

                    {expandedFeedback === feedback.id && (
                      <div className="feedback-full">
                        <p className="feedback-page">
                          <strong>Página:</strong> {feedbackService.getPageDisplayName(feedback.page)}
                        </p>
                        <p className="feedback-full-text">
                          {feedback.feedbackText}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* TD3-122: Danger Zone - Account Deletion */}
          <section className="settings-section danger-zone">
            <h2>⚠️ Atenção</h2>
            <p className="danger-zone-warning">
              A exclusão da conta é permanente e não pode ser desfeita após 30 dias.
            </p>
            <button
              onClick={handleDeleteAccountClick}
              className="delete-account-btn danger-cta"
            >
              Excluir Minha Conta
            </button>
          </section>
        </main>

        <footer className="settings-footer">
          <p>
            Oráculo dos 12 Raios • v1.0 • by Buenaar
            {' • '}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-tos-link"
            >
              Termos de Serviço
            </a>
          </p>
        </footer>
      </div>

      {/* TD3-122: Warning Modal (Step 1) */}
      {showDeleteWarning && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content delete-warning-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Tem certeza que deseja excluir sua conta?</h2>
            <p className="warning-text">
              Esta ação é permanente e não pode ser desfeita. Todos os seus dados serão removidos em 30 dias.
            </p>
            <ul className="deletion-consequences">
              <li>Você perderá acesso aos seus oráculos anteriores</li>
              <li>Esta ação não pode ser revertida</li>
            </ul>
            <div className="modal-actions">
              <button
                onClick={handleCancelDelete}
                className="secondary-cta"
              >
                Cancelar
              </button>
              <button
                onClick={handleContinueToPasswordConfirm}
                className="danger-cta"
              >
                Continuar com Exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TD3-122: Password Confirmation Modal (Step 2) */}
      {showPasswordConfirm && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content password-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Digite sua senha para confirmar</h2>

            {deleteError && (
              <div className="error-message">
                {deleteError}
              </div>
            )}

            <div className="password-confirm-field">
              <label>Senha</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Digite sua senha"
                disabled={isDeletingAccount}
                className="password-input"
                autoFocus
              />
            </div>

            <div className="modal-actions">
              <button
                onClick={handleCancelDelete}
                disabled={isDeletingAccount}
                className="secondary-cta"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeletingAccount}
                className="danger-cta"
              >
                {isDeletingAccount ? 'Excluindo...' : 'Excluir Conta Permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TD3-105: Feedback System */}
      <FeedbackButton onClick={() => setShowFeedbackModal(true)} />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        currentPage="settings"
      />

      {/* TD3-129: Logout Overlay with Fadeout (LGPD Compliance) */}
      {showLogoutOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          opacity: overlayFading ? 0 : 1,
          transition: 'opacity 2s ease-out'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '90%',
            color: 'white',
            fontFamily: 'var(--font-primary)',
            textAlign: 'center',
            padding: '3rem'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '2rem',
              animation: 'pulse 2s ease-in-out infinite'
            }}>✅</div>

            <h1 style={{
              fontSize: '2.5rem',
              marginBottom: '2rem',
              fontWeight: 600,
              color: '#10B981'
            }}>
              Solicitação Enviada!
            </h1>

            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '1.5rem', marginRight: '1rem' }}>1️⃣</span>
                <strong style={{ fontSize: '1.1rem' }}>Verifique a caixa de entrada do SEU NOVO E-MAIL</strong>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '1.5rem', marginRight: '1rem' }}>2️⃣</span>
                <strong style={{ fontSize: '1.1rem' }}>Clique no link de verificação</strong>
              </div>

              <div>
                <span style={{ fontSize: '1.5rem', marginRight: '1rem' }}>3️⃣</span>
                <strong style={{ fontSize: '1.1rem' }}>Faça login novamente com o novo e-mail</strong>
              </div>
            </div>

            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.7)',
              fontStyle: 'italic',
              marginBottom: '2rem'
            }}>
              ⚠️ Seu e-mail antigo recebeu uma notificação de segurança
            </p>

            <p style={{
              fontSize: '1.2rem',
              color: '#10B981',
              fontWeight: 600
            }}>
              Fazendo logout automaticamente...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
